import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'

import { breakpoints, colors, fontSizes } from '@/styles'
import {
  Accordion,
  AccordionPanel,
  AccordionTitle
} from '@/components/accordion'
import { DownArrowWhiteIcon } from '@/components/icons'

const HEADER_HEIGHT = 80

const Wrapper = styled.div`
  :first-child {
    margin-top: 70px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    :first-child {
      margin-top: 0px;
    }
  }
`

const AccordionContainer = styled.section`
  border-bottom: 1px solid ${colors.grey};
  :last-child {
    border-bottom: 0;
  }
`

const AccordionRow = styled.div`
  padding: 10px 20px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 10px 12px;
  }
`

const AccordionContent = styled.div`
  display: flex;
  flex-direction: column;
`

const AccordionTitleBar = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  padding: 0 20px;
  scroll-margin-top: ${HEADER_HEIGHT}px;

  svg path {
    fill: #494949;
  }
  svg {
    transition: all 0.3s ease-in-out;
    transform: rotate(-90deg);
    flex-shrink: 0;

    ${props =>
      props.active &&
      `
      transform: rotate(0deg)
    `}
  }

  :hover {
    background-color: ${props => (props.active ? 'transparent' : '#e9f2fc')};
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 10px 12px;
  }
`

const Label = styled.p`
  font-size: 24px;
  color: ${colors.regular};
  line-height: 33px;
  letter-spacing: -0.5px;
  margin: 30px 0px;
  padding-right: 50px;
  cursor: pointer;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 18px;
    line-height: 24px;
    margin: 15px 0px;
    padding-right: 20px;
  }
`

const ItemContainer = styled.div`
  display: inline-block;
  padding-bottom: 20px;
`

const textStyle = css`
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  font-size: 18px;
  font-weight: 600;
  color: ${colors.darkGrey};
  line-height: 24px;
  letter-spacing: -0.2px;
  display: inline;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: ${fontSizes.small};
    line-height: 19px;
  }
`

const AnswerHeading = styled.p`
  ${textStyle}
  padding-bottom: 12px;
  font-weight: 700;
`

export const ItemLink = styled.a`
  ${textStyle}
  word-wrap: break-word;
  color: ${colors.link};
  padding: 0;
  background: none;
  text-decoration: underline;
`

const ItemText = styled.p`
  ${textStyle}
`

const FaqAccordion = ({ data }) => {
  const { asPath } = useRouter()
  const routerHash = asPath.split('#')[1]

  return (
    <Wrapper>
      {data.map(tip => (
        <Accordion key={tip.title} isActive={routerHash === tip.path}>
          <AccordionContainer>
            <AccordionTitle>
              <AccordionTitleBar id={tip.path}>
                <Label>{tip.title}</Label>
                <DownArrowWhiteIcon width={15} height={12} />
              </AccordionTitleBar>
            </AccordionTitle>
            <AccordionPanel>
              <AccordionRow>
                {tip.answers &&
                  tip.answers.map((answer, answerIndex) => (
                    <AccordionContent key={`${tip.subtitle}-${answerIndex}`}>
                      {answer.heading && (
                        <AnswerHeading>{answer.heading}</AnswerHeading>
                      )}
                      {answer.steps && (
                        <ItemContainer>
                          {answer.steps.map((step, stepIndex) => {
                            if (step?.href) {
                              return (
                                <ItemLink
                                  key={`${step}-${stepIndex}`}
                                  href={step.href}
                                  target="_blank"
                                >
                                  {step.text}
                                </ItemLink>
                              )
                            } else if (step?.onClick) {
                              return (
                                <ItemLink
                                  key={`${step}-${stepIndex}`}
                                  onClick={step.onClick}
                                  as="button"
                                  type="button"
                                >
                                  {step.text}
                                </ItemLink>
                              )
                            } else {
                              return (
                                <ItemText key={`${step}-${stepIndex}`}>
                                  {step}
                                </ItemText>
                              )
                            }
                          })}
                        </ItemContainer>
                      )}
                    </AccordionContent>
                  ))}
              </AccordionRow>
            </AccordionPanel>
          </AccordionContainer>
        </Accordion>
      ))}
    </Wrapper>
  )
}

export default FaqAccordion
